import { Box, ClickAwayListener, Divider, Popper, Stack, Typography } from '@mui/material';
import { yellow } from '@mui/material/colors';
import dayjs from 'dayjs';
import { useState, type JSX } from 'react';
import CountUp from 'react-countup';
import type { JackpotPool, StyleObj } from '../../@types';
import useBreakpoints from '../../hooks/useBreakpoints';
import useLocalization from '../../hooks/useLocalization';
import { useJackpotPoolStatistics } from '../../queries/jackpots';
import { CurrencySymbol } from '../atoms/CurrencySymbol';
import ArrowIcon from '../icons/ArrowIcon';
import DiamondIcon from '../icons/DiamondIcon';

export const JACKPOT_COLOR = yellow[500];

const borderStyles = {
  border: '1px solid',
  borderColor: JACKPOT_COLOR,
};

const fireBackground = {
  backgroundImage: 'url(/fire.svg)',
  backgroundColor: 'rgb(0,0,0,0.6)',
  backgroundPosition: 'center',
  backgroundBlendMode: 'overlay',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
};

const styles: StyleObj = {
  container: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    ...borderStyles,
    borderRadius: 1,
    cursor: 'pointer',
    px: { xs: 1, md: 2 },
    py: 0.75,
    position: 'relative',
  },
  name: {
    typography: { xs: 'body3', md: 'body2' },
    color: 'neutral.100',
  },
  amountFake: {
    typography: { xs: 'h5', lg: 'h4' },
    fontWeight: 700,
    minWidth: 'fit-content',
    opacity: 0,
  },
  amount: {
    typography: { xs: 'h5', lg: 'h4' },
    fontWeight: 700,
    minWidth: 'fit-content',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  divider: {
    width: 2,
    height: 24,
    background: (theme) =>
      `linear-gradient(180deg, rgba(254, 218, 43, 0.00) 0%, ${theme.palette.info[600]} 50%, rgba(254, 218, 43, 0.00) 100%)`,
  },
  popperContainer: {
    ...borderStyles,
    borderRadius: 2,
    backgroundColor: 'neutral.800',
    mt: 2,
  },
  popperArrow: {
    position: 'absolute',
    top: 2,
    left: 30,
  },
  popperContent: {
    p: {
      xs: 1,
      md: 2,
    },
    gap: 2,
  },
  popperText: {
    typography: {
      xs: 'body4',
      md: 'body2',
    },
    color: 'neutral.200',
    '> span': {
      typography: {
        xs: 'body4',
        md: 'body2',
      },
    },
  },
  popperHighlightedText: {
    typography: {
      xs: 'body4',
      md: 'body2',
    },
    color: JACKPOT_COLOR,
  },
  diamondContainer: {
    flexDirection: 'row',
    alignSelf: 'center',
  },
  diamondAmount: {
    typography: {
      xs: 'h6',
      md: 'h4',
    },
    ml: 1,
  },
  dividerFull: {
    backgroundColor: JACKPOT_COLOR,
  },
};

type Props = {
  jackpotPool: JackpotPool;
  icon: JSX.Element;
  maxAmount: number;
};

const JackpotInfo = ({ jackpotPool, icon, maxAmount }: Props) => {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorElement);
  const { isMobile } = useBreakpoints();
  const { t } = useLocalization();
  const { data: jackpotStatistics } = useJackpotPoolStatistics(jackpotPool.jackpotId, open);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElement(anchorElement ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  return (
    <>
      <Stack
        direction='row'
        onClick={handleClick}
        sx={{ ...styles.container, ...(jackpotPool.isActive && fireBackground) }}
        data-testid={`jackpot-pool-${jackpotPool.id}`}
      >
        <Stack
          sx={{
            alignItems: 'center',
          }}
        >
          {icon}
        </Stack>
        {!isMobile && (
          <Typography sx={styles.name} noWrap>
            {jackpotPool.name}
          </Typography>
        )}
        <Stack
          direction='row'
          spacing={1}
          sx={{
            alignItems: 'center',
            position: 'relative',
          }}
        >
          <Typography sx={styles.amountFake} noWrap>
            {maxAmount.toFixed(3)}
            <CurrencySymbol />
          </Typography>
          <Typography sx={styles.amount} noWrap>
            <CountUp end={jackpotPool.amount} decimals={2} duration={3} preserveValue />
            <CurrencySymbol />
          </Typography>
        </Stack>
      </Stack>

      <Popper id={jackpotPool.id} open={open} anchorEl={anchorElement} sx={{ zIndex: 10 }}>
        <ClickAwayListener onClickAway={handleClose}>
          <Stack
            sx={styles.popperContainer}
            onClick={handleClose}
            data-testid={`expanded-jackpot-pool-${jackpotPool.id}`}
          >
            <Box sx={styles.popperArrow} data-testid='sportsbook-jackpot-popper-arrow'>
              <ArrowIcon />
            </Box>
            {isMobile && (
              <>
                <Typography sx={{ ...styles.popperText, textAlign: 'center', py: 1 }}>{jackpotPool.name}</Typography>
                <Divider sx={styles.dividerFull} />
              </>
            )}
            <Stack sx={styles.popperContent}>
              <Typography sx={styles.popperText}>
                {t('largestWinner')}:{' '}
                {jackpotStatistics?.largestWinner && dayjs(jackpotStatistics.largestWinner.wonAt).format('DD/MM/YYYY')}
              </Typography>
              <Stack sx={styles.diamondContainer}>
                <DiamondIcon />
                <Typography component='span' sx={styles.diamondAmount}>
                  {jackpotStatistics?.largestWinner?.amount?.toFixed(2)}
                  <CurrencySymbol />
                </Typography>
              </Stack>
            </Stack>
            <Box sx={styles.popperContent}>
              <Typography sx={styles.popperText}>
                {t('numberOfWinners')}:{' '}
                <Typography
                  component='span'
                  color={JACKPOT_COLOR}
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {jackpotStatistics?.totalWinnersCount}
                </Typography>
              </Typography>
            </Box>
            <Divider sx={styles.dividerFull} />
            <Stack sx={styles.popperContent}>
              <Typography sx={styles.popperText}>
                {t('latestWinner')}:{' '}
                {jackpotStatistics?.latestWinner && dayjs(jackpotStatistics.latestWinner.wonAt).format('DD/MM/YYYY')}
              </Typography>
              <Typography sx={styles.popperHighlightedText}>{jackpotStatistics?.latestWinner?.winnerName}</Typography>
              <Stack sx={styles.diamondContainer}>
                <DiamondIcon />
                <Typography component='span' sx={styles.diamondAmount}>
                  {jackpotStatistics?.latestWinner?.amount?.toFixed(2)}
                  <CurrencySymbol />
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default JackpotInfo;
